'use client';

/**
 * PartnerDiscountCode - Compact referral code display with copy and regenerate functionality
 * Bubble design aesthetic - subtle, not overpowering
 *
 * Features:
 * - Copy code to clipboard
 * - Copy shareable link
 * - Regenerate code (owner only) with confirmation
 * - Auto-create code if none exists
 */

import React, { useState, useEffect, useTransition } from 'react';
import { Gift, Copy, Check, Link2, ExternalLink, RefreshCw, Plus, Loader2 } from 'lucide-react';
import { captureClientError } from '@/lib/error-reporting/client';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { regenerateReferralCode } from '../actions/regenerate-referral-code';
import type { ReferralCode } from '../types';

interface PartnerDiscountCodeProps {
  referralCode: ReferralCode | null;
}

export function PartnerDiscountCode({ referralCode: initialCode }: PartnerDiscountCodeProps) {
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(initialCode);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [appUrl, setAppUrl] = useState('');
  const [isPending, startTransition] = useTransition();
  const [dialogOpen, setDialogOpen] = useState(false);

  // window.location.origin is client-only — read it AFTER mount so server and client
  // first-render both start with '' (SSR-safe + hydration-stable). A lazy
  // useState(() => window.location.origin) initializer would throw on the SSR pass.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: window is client-only, read post-mount
    setAppUrl(window.location.origin);
  }, []);

  // Sync with prop changes (external mutation e.g. regenerate returns new code from server)
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: syncing local cache from server-returned prop after mutation
    setReferralCode(initialCode);
  }, [initialCode]);

  const referralLink = referralCode?.code ? `${appUrl}/auth/register?ref=${referralCode.code}` : '';

  const copyToClipboard = async (text: string, type: 'code' | 'link') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'code') {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } else {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
    } catch (_) {
      // intentional: Clipboard API failed - use fallback execCommand method
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      if (type === 'code') {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } else {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }
    }
  };

  const handleRegenerate = () => {
    startTransition(async () => {
      const result = await regenerateReferralCode();

      if (!result.success) {
        toast.error(result.error || 'Failed to regenerate code');
        return;
      }

      setReferralCode(result.data.referralCode);
      toast.success('Partner code regenerated successfully');
      setDialogOpen(false);
    });
  };

  const handleCreateCode = () => {
    startTransition(async () => {
      const result = await regenerateReferralCode();

      if (!result.success) {
        toast.error(result.error || 'Failed to create code');
        return;
      }

      setReferralCode(result.data.referralCode);
      toast.success('Partner code created successfully');
    });
  };

  // No code exists - show create button
  if (!referralCode) {
    return (
      <div className="rounded-[1.5rem] bg-white/50 dark:bg-zinc-900/30 backdrop-blur-xl border border-zinc-200/60 dark:border-zinc-800/60 px-5 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Gift className="h-4 w-4 text-amber-500" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
                Partner Code
              </p>
              <p className="text-[10px] text-zinc-500 dark:text-zinc-600">
                1 Month Free for clients
              </p>
            </div>
          </div>

          <div className="flex flex-1">
            <Button
              onClick={handleCreateCode}
              disabled={isPending}
              className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border border-amber-500/30"
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Generate Your Partner Code
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[1.5rem] bg-white/50 dark:bg-zinc-900/30 backdrop-blur-xl border border-zinc-200/60 dark:border-zinc-800/60 px-5 py-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Left: Icon + Label */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center">
            <Gift className="h-4 w-4 text-amber-500" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
              Partner Code
            </p>
            <p className="text-[10px] text-zinc-500 dark:text-zinc-600">1 Month Free for clients</p>
          </div>
        </div>

        {/* Right: Copy boxes */}
        <div className="flex flex-1 gap-3">
          {/* Copy Code */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-100/60 dark:bg-zinc-800/40 border border-zinc-200/60 dark:border-zinc-700/40 flex-1">
            <span className="font-mono font-bold text-sm text-zinc-900 dark:text-white tracking-wider">
              {referralCode.code}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                copyToClipboard(referralCode.code, 'code').catch((error) => {
                  captureClientError(error, 'client_runtime', {
                    component: 'PartnerDiscountCode',
                    action: 'copy_code_to_clipboard',
                  }).catch(() => {});
                });
              }}
              className="h-7 w-7 p-0 ml-auto hover:bg-zinc-200 dark:hover:bg-zinc-700"
            >
              {copiedCode ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5 text-zinc-400" />
              )}
            </Button>

            {/* Regenerate Button with Confirmation */}
            <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isPending}
                  className="h-7 w-7 p-0 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                  title="Regenerate partner code"
                >
                  {isPending ? (
                    <Loader2 className="h-3.5 w-3.5 text-zinc-400 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 text-zinc-400" />
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="glass-popover">
                <AlertDialogHeader>
                  <AlertDialogTitle>Regenerate Partner Code?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will create a new partner code based on your current organization name.
                    <span className="block mt-2 text-amber-500 font-medium">
                      Warning: Your existing code will stop working immediately. Any links
                      you&apos;ve shared will no longer give users the free trial.
                    </span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => {
                      e.preventDefault();
                      handleRegenerate();
                    }}
                    disabled={isPending}
                    className="bg-amber-500 hover:bg-amber-600 text-black"
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Regenerating...
                      </>
                    ) : (
                      'Regenerate Code'
                    )}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* Copy Link */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/5 border border-amber-500/20 flex-1">
            <Link2 className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
            <span className="text-xs text-zinc-500 truncate hidden sm:block">Shareable Link</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                copyToClipboard(referralLink, 'link').catch((error) => {
                  captureClientError(error, 'client_runtime', {
                    component: 'PartnerDiscountCode',
                    action: 'copy_link_to_clipboard',
                  }).catch(() => {});
                });
              }}
              className="h-7 w-7 p-0 ml-auto hover:bg-amber-500/10"
            >
              {copiedLink ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <ExternalLink className="h-3.5 w-3.5 text-amber-400" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
